<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { t } from '@/i18n/utils'

interface Props {
  text: string
  lineClamp?: number
  textClass?: string
  moreText?: string
  lessText?: string
  moreClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  lineClamp: 3,
  textClass: 'text-14px lh-22px text-[#CCCCCC]',
  moreText: undefined,
  lessText: undefined,
  moreClass: 'text-n-primary cursor-pointer text-14px',
})

const isExpanded = ref(false)
const textRef = ref<HTMLParagraphElement>()
const shouldShowToggle = ref(false)

// 检查文本是否超过指定行数
onMounted(() => {
  if (textRef.value) {
    // 暂时移除 line-clamp 以获取真实高度
    const lineHeight = Number.parseFloat(getComputedStyle(textRef.value).lineHeight)
    const maxHeight = lineHeight * props.lineClamp
    const actualHeight = textRef.value.scrollHeight

    // 如果实际高度大于最大高度，说明文本超过了指定行数
    shouldShowToggle.value = actualHeight > maxHeight + 2 // +2 为容错
  }
})

function toggle() {
  isExpanded.value = !isExpanded.value
}

// 计算文本类名
const textClasses = computed(() => {
  const classes = [props.textClass]
  if (!isExpanded.value && shouldShowToggle.value)
    classes.push(`line-clamp-${props.lineClamp}`)

  return classes.join(' ')
})

const moreTextComputed = computed(() => props.moreText ?? t('BookIntroduce'))
const lessTextComputed = computed(() => props.lessText ?? t('ShowLess'))
</script>

<template>
  <div class="relative">
    <p
      ref="textRef"
      :class="textClasses"
    >
      {{ text }}
    </p>
    <!-- 收起状态：带渐变背景的 More 按钮 -->
    <span
      v-if="!isExpanded && shouldShowToggle"
      :class="moreClass"
      class="absolute bottom-0 right-0"
      style="background: linear-gradient(to right, transparent, black 20%, black); padding-left: 2em;"
      @click="toggle"
    >
      {{ moreTextComputed }}
    </span>
    <!-- 展开状态：普通的 Less 按钮 -->
    <span
      v-else-if="isExpanded && shouldShowToggle"
      :class="moreClass"
      class="inline-block mt-5px"
      @click="toggle"
    >
      {{ lessTextComputed }}
    </span>
  </div>
</template>
